import { Box, Container, Typography, Grid, Chip } from '@mui/material';
import { Languages, Check } from 'lucide-react';
import Reveal from './Reveal';
import { tokens } from '../theme';

/**
 * Multi-language coverage — calls + messages (TASK K).
 * No language-count claims by design: chips + an ellipsis chip instead.
 */

const languageChips = [
  'English',
  'Español',
  'Français',
  'Deutsch',
  'Italiano',
  'Português',
  'Українська',
  'Polski',
  '中文',
  'العربية',
  '…and more',
];

const transcript = [
  {
    from: 'customer',
    lang: 'ES',
    text: '¿Tienen mesa para cuatro este viernes a las ocho?',
  },
  {
    from: 'ai',
    lang: 'ES',
    text: 'Sí — tenemos a las 20:00 y a las 20:30. ¿Cuál prefiere?',
  },
  {
    from: 'customer',
    lang: 'FR',
    text: 'Et le menu végétarien, il est disponible ?',
  },
  {
    from: 'ai',
    lang: 'FR',
    text: 'Bien sûr ! Notre menu végétarien complet est servi tous les jours.',
  },
];

const bullets = [
  'Auto-detects the language and replies in it — mid-conversation switches included',
  'Cross-language understanding: a Spanish question finds the answer in your English menu',
  'Works the same on calls, chat, SMS, and DMs',
];

export default function MultiLanguageSection() {
  return (
    <Box
      id="languages"
      sx={{
        py: { xs: 8, md: 12 },
        bgcolor: tokens.bandAlt,
        borderTop: `1px solid ${tokens.border}`,
        borderBottom: `1px solid ${tokens.border}`,
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 5, md: 8 }} alignItems="center">
          {/* Copy side */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Reveal>
              <Typography variant="overline" sx={{ display: 'block', mb: 1 }}>
                Multi-language
              </Typography>
              <Typography variant="h2" sx={{ mb: 2 }}>
                AI speaks your customer's language
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: 'text.secondary', mb: 3, lineHeight: 1.7 }}
              >
                Your knowledge base stays in one language. Your customers don't
                have to. The AI detects what each caller or texter speaks,
                answers natively, and still finds the right answer in your
                docs — semantic matching routes intent across languages.
              </Typography>
              <Box sx={{ mb: 3.5 }}>
                {bullets.map((b) => (
                  <Box
                    key={b}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.2,
                      py: 1.2,
                      borderTop: `1px solid ${tokens.border}`,
                      '&:last-of-type': { borderBottom: `1px solid ${tokens.border}` },
                    }}
                  >
                    <Box sx={{ color: tokens.sage, mt: '2px', display: 'flex', flexShrink: 0 }}>
                      <Check size={16} strokeWidth={1.5} />
                    </Box>
                    <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem', lineHeight: 1.55 }}>
                      {b}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {languageChips.map((lang) => (
                  <Chip
                    key={lang}
                    label={lang}
                    size="small"
                    sx={{
                      borderRadius: '999px',
                      bgcolor: tokens.bgPaper,
                      border: `1px solid ${tokens.border}`,
                      color: 'text.secondary',
                      fontWeight: 500,
                      fontSize: '0.78rem',
                    }}
                  />
                ))}
              </Box>
            </Reveal>
          </Grid>

          {/* Transcript side */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Reveal delay={tokens.motion.stagger}>
              <Box
                sx={{
                  borderRadius: `${tokens.radiusCard}px`,
                  bgcolor: tokens.bgPaper,
                  border: `1px solid ${tokens.border}`,
                  boxShadow: tokens.shadowSoft,
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    px: 2.5,
                    py: 1.5,
                    bgcolor: tokens.bgDefault,
                    borderBottom: `1px solid ${tokens.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Languages size={16} strokeWidth={1.5} color={tokens.copper} />
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'text.primary' }}>
                    One conversation, two languages
                  </Typography>
                  <Typography
                    sx={{
                      ml: 'auto',
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      color: tokens.sage,
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                    }}
                  >
                    Live
                  </Typography>
                </Box>
                <Box sx={{ p: 2.5 }}>
                  {transcript.map((msg, i) => {
                    const isAI = msg.from === 'ai';
                    return (
                      <Box
                        key={i}
                        sx={{
                          display: 'flex',
                          justifyContent: isAI ? 'flex-start' : 'flex-end',
                          mb: i < transcript.length - 1 ? 1.5 : 0,
                        }}
                      >
                        <Box
                          sx={{
                            maxWidth: '85%',
                            px: 1.8,
                            py: 1.1,
                            borderRadius: '12px',
                            bgcolor: isAI ? tokens.sageTint : tokens.bgDefault,
                            border: `1px solid ${
                              isAI ? 'rgba(47, 93, 80, 0.25)' : tokens.border
                            }`,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.4 }}>
                            <Typography
                              sx={{
                                fontSize: '0.66rem',
                                fontWeight: 700,
                                letterSpacing: '0.1em',
                                color: isAI ? tokens.sage : tokens.copper,
                              }}
                            >
                              {isAI ? 'CLEOLEAD AI' : 'CUSTOMER'}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '0.64rem',
                                fontWeight: 600,
                                px: 0.7,
                                py: 0.1,
                                borderRadius: '999px',
                                bgcolor: isAI ? 'rgba(47, 93, 80, 0.12)' : tokens.copperTint,
                                color: isAI ? tokens.sage : tokens.copperDark,
                              }}
                            >
                              {msg.lang}
                            </Typography>
                          </Box>
                          <Typography sx={{ fontSize: '0.85rem', color: 'text.primary', lineHeight: 1.5 }}>
                            {msg.text}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Reveal>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
